import { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Fade, Slide } from '@mui/material';
import { theme } from '@/theme';
import { AppState } from '@/types';
import { appStateService } from '@/services/appStateService';
import { offlineStorageService } from '@/services/offlineStorage';
import { syncService } from '@/services/syncService';
import { geolocationService } from '@/services/geolocationService';
import { LoginForm } from '@/components/LoginForm';
import { Dashboard } from '@/components/Dashboard';
import { DonationForm } from '@/components/DonationForm';
import { NewDonationWizard } from '@/components/NewDonationWizard';
import { DonationsList } from '@/components/DonationsList';
import { NotificationContainer } from '@/components/NotificationContainer';
import { CreditsPage } from '@/components/CreditsPage';
import { ElectronicDonationPage } from '@/components/ElectronicDonationPage';


type AppView = 'login' | 'dashboard' | 'donation-form' | 'new-donation' | 'donations-list' | 'electronic-donation' | 'credit';

function App() {
  const [appState, setAppState] = useState<AppState>(appStateService.getState());
  const [currentView, setCurrentView] = useState<AppView>('login');
  const [isInitializing, setIsInitializing] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    // Inizializza servizi
    const initializeApp = async () => {
      try {
        // Inizializza storage offline
        await offlineStorageService.init();
        
        // NOTA: Non avviamo automaticamente il monitoraggio geolocalizzazione
        // per evitare violazioni di sicurezza del browser. Il monitoraggio
        // verrà avviato solo quando l'utente interagisce esplicitamente.
        
        // Verifica se l'utente è già autenticato
        if (appState.isAuthenticated) {
          setCurrentView('dashboard');
        }
      } catch (error) {
        console.error('App initialization failed:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeApp();

    // Listener per aggiornamenti stato app
    const handleStateChange = (newState: AppState) => {
      setAppState(newState);
    };

    appStateService.addStateListener(handleStateChange);

    // Listener per sincronizzazione automatica
    const handleOnline = () => {
      syncService.autoSync();
    };

    window.addEventListener('online', handleOnline);

    // Cleanup
    return () => {
      appStateService.removeStateListener(handleStateChange);
      window.removeEventListener('online', handleOnline);
      geolocationService.stopWatching();
    };
  }, []);

  const handleLoginSuccess = () => {
    setCurrentView('dashboard');
  };


  const handleCreateDonation = () => {
    handleNavigate('new-donation');
  };

  const handleViewDonations = () => {
    handleNavigate('donations-list');
  };

  const handleElectronicDonation = () => {
    handleNavigate('electronic-donation');
  };

  const handleDonationSuccess = () => {
    handleNavigate('dashboard');
  };

  const handleNewDonationSuccess = () => {
    handleNavigate('dashboard');
  };

  const handleBackToDashboard = () => {
    handleNavigate('dashboard');
  };

  const handleNavigateToCredits = () => {
    handleNavigate('credit');
  };


  // Funzione di navigazione generale per il menu con transizioni fluide
  const handleNavigate = (view: string) => {
    if (isTransitioning) return; // Previeni navigazioni multiple durante la transizione
    
    setIsTransitioning(true);
    
    // Fade out della vista corrente
    setTimeout(() => {
      switch (view) {
        case 'dashboard':
          setCurrentView('dashboard');
          break;
        case 'new-donation':
          setCurrentView('new-donation');
          break;
        case 'donations-list':
          setCurrentView('donations-list');
          break;
        case 'electronic-donation':
          setCurrentView('electronic-donation');
          break;
        case 'credit':
          setCurrentView('credit');
          break;
        default:
          setCurrentView('dashboard');
      }
      
      // Fade in della nuova vista
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }, 200);
  };

  // Mostra loading durante inizializzazione
  if (isInitializing) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        }}>
          <div style={{
            textAlign: 'center',
            color: 'white',
          }}>
            <div style={{
              width: 60,
              height: 60,
              border: '4px solid rgba(255,255,255,0.3)',
              borderTop: '4px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px',
            }} />
            <h2>Field App</h2>
            <p>Inizializzazione...</p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </ThemeProvider>
    );
  }

  // Mostra login se non autenticato
  if (!appState.isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginForm onLoginSuccess={handleLoginSuccess} />
        <NotificationContainer />
      </ThemeProvider>
    );
  }

  // Renderizza vista corrente con transizioni fluide unificate
  const renderCurrentView = () => {
    // Tutte le pagine entrano da destra verso sinistra (direction="left")
    const renderPageWithTransition = (component: React.ReactNode) => (
      <Fade in={!isTransitioning} timeout={300}>
        <Slide direction="left" in={!isTransitioning} timeout={400}>
          <div>
            {component}
          </div>
        </Slide>
      </Fade>
    );

    switch (currentView) {
      case 'dashboard':
        return renderPageWithTransition(
          <Dashboard
            onCreateDonation={handleCreateDonation}
            onViewDonations={handleViewDonations}
            onElectronicDonation={handleElectronicDonation}
            onNavigateToCredits={handleNavigateToCredits}
          />
        );
      case 'donation-form':
        return renderPageWithTransition(
          <DonationForm
            onSuccess={handleDonationSuccess}
          />
        );
      case 'new-donation':
        return renderPageWithTransition(
          <NewDonationWizard
            onSuccess={handleNewDonationSuccess}
            onCancel={handleBackToDashboard}
            onNavigate={handleNavigate}
          />
        );
      case 'donations-list':
        return renderPageWithTransition(
          <DonationsList
            onNavigate={handleNavigate}
          />
        );
      case 'electronic-donation':
        return renderPageWithTransition(
          <ElectronicDonationPage 
            onBack={handleBackToDashboard}
            onNavigate={handleNavigate}
          />
        );
      case 'credit':
        return renderPageWithTransition(
          <CreditsPage 
            onNavigate={handleNavigate}
          />
        );
      default:
        return renderPageWithTransition(
          <Dashboard
            onCreateDonation={handleCreateDonation}
            onViewDonations={handleViewDonations}
            onElectronicDonation={handleElectronicDonation}
            onNavigateToCredits={handleNavigateToCredits}
          />
        );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {renderCurrentView()}
      <NotificationContainer />
    </ThemeProvider>
  );
}

export default App;
